import {RankedModeTypes} from '../../../../../../../shared/GameEnums.ts'
import {LeaderboardConstants} from '../../../../../../../shared/leaderboard_data/LeaderboardConstants.ts'
import {LeaderboardCategoryStringID} from '../../../../../../../shared/leaderboard_data/LeaderboardEnums.ts'
import {type LeaderboardTableTabData, type LeaderboardRowData, type LeaderboardStickyRowData, type LeaderboardTabData, type RankedSeasonData} from '../../../../../../../shared/leaderboard_data/LeaderboardInterfaces.ts'
import {TimeUtils} from '../../../../../../../shared/utils/TimeUtils.ts'
import {UIAjaxWrapper} from '../../../../../../shared/scripts/network/UIAjaxWrapper.ts'
import {UIAPIEndpoints} from '../../../../../../shared/scripts/network/UIAPIEndpoints.ts'
import {UIPopupController} from '../../../../../../shared/scripts/ui_popup/UIPopupController.ts'
import {UILeaderboardStickyRowController} from '../../../../../../shared/scripts/ui_leaderboard/UILeaderboardStickyRowController.ts'
import {UILeaderboardTableController} from '../../../../../../shared/scripts/ui_leaderboard/UILeaderboardTableController.ts'
import {UILeaderboardUtils} from '../../../../../../shared/scripts/ui_leaderboard/UILeaderboardUtils.ts'
import {UIMLeaderboardSearchController} from './UIMLeaderboardSearchController.ts'
import {type TimeoutId} from '@colonist/types'
import {navigateToPage} from '../../../../../../shared/scripts/ui_utils/ClientUtils.ts'
import {SeasonProgress, TableTabs, RankedPageTabsContainer, RankedPageTabIds} from '@colonist/ui-platform'
import {type UIReactComponentViewOf, mountUIReactComponent} from '../../../../../../shared/scripts/ui_platform/mountUIReactComponent.tsx'

export class UIMLeaderboardController {
    private leaderboardUrl: string = LeaderboardConstants.globalLeaderboardUrl
    private rankedModeType: RankedModeTypes = RankedModeTypes.Classic4P
    private tableTabs: LeaderboardTableTabData[] = []
    private readonly tableController: UILeaderboardTableController
    private readonly stickyRowController: UILeaderboardStickyRowController
    private readonly rankedTabsView: UIReactComponentViewOf<typeof RankedPageTabsContainer>
    private readonly tableTabsView: UIReactComponentViewOf<typeof TableTabs>
    private readonly seasonProgressView: UIReactComponentViewOf<typeof SeasonProgress>
    private searchTerm: string
    private enabledTabIds: string[] = [RankedPageTabIds.Classic4P, RankedPageTabIds.Classic1v1, RankedPageTabIds.CitiesAndKnights4P]
    private showRushTab: boolean = false
    private isLoadingDataAfterScroll: boolean = false
    private waitForBackend: TimeoutId | undefined

    constructor() {
        UIMLeaderboardController.featureCheck()
        this.stickyRowController = new UILeaderboardStickyRowController()
        this.tableController = new UILeaderboardTableController(() => this.fetchScrollData(), this.stickyRowController)
        this.rankedTabsView = mountUIReactComponent('leaderboard_ranked_tabs', RankedPageTabsContainer)
        this.tableTabsView = mountUIReactComponent('leaderboard_tabs', TableTabs)
        this.seasonProgressView = mountUIReactComponent('leaderboard_season_progress', SeasonProgress)

        this.initiateParametersFromUrl()
        void this.fetchNewTabs()
        void this.fetchMyStickyRowData()
        void this.initRushTab()

        new UIMLeaderboardSearchController((searchTerm: string) => {
            this.onSearchChange(searchTerm)
            return undefined
        })

        this.renderRankedTabs()
        this.stickyRowController.setSearchValue(this.searchTerm)

        this.switchRankingCategoryTypeData(this.rankedModeType, this.leaderboardUrl, this.searchTerm, false)
    }

    private onSearchChange(searchValue: string) {
        this.switchRankingCategoryTypeData(this.rankedModeType, this.leaderboardUrl, searchValue)
        void this.fetchMyStickyRowData()
        this.stickyRowController.setSearchValue(searchValue)
        this.tableController.updateStickyRowStyle()
    }

    private onRankedModeChange(rankedMode: RankedModeTypes) {
        this.switchRankingCategoryTypeData(rankedMode, this.leaderboardUrl, this.searchTerm)
        void this.fetchMyStickyRowData()
    }

    private onHorizontalTabChange(leaderboardUrl: string) {
        this.switchRankingCategoryTypeData(this.rankedModeType, leaderboardUrl, this.searchTerm)
        if(leaderboardUrl == LeaderboardConstants.globalLeaderboardUrl) void this.fetchNewTabs()
        void this.fetchMyStickyRowData()
    }

    private onLeaderboardUrlChange(newLeaderboardUrl: string) {
        this.switchRankingCategoryTypeData(this.rankedModeType, newLeaderboardUrl, this.searchTerm)
        void this.fetchNewTabs()
        void this.fetchMyStickyRowData()
    }

    private initiateParametersFromUrl() {
        this.rankedModeType = RankedModeTypes.Classic4P
        const url = new URL(window.location.href)
        const pathSegments = url.pathname.split('/')
        const leaderboardPathSegmentIndex = pathSegments.indexOf('leaderboards')
        const rankedModeType = pathSegments[leaderboardPathSegmentIndex + 1]
        if(RankedModeTypes[rankedModeType] !== undefined) this.rankedModeType = RankedModeTypes[rankedModeType]
        this.leaderboardUrl = LeaderboardConstants.globalLeaderboardUrl
        this.searchTerm = ''
    }

    private switchRankingCategoryTypeData(mode: RankedModeTypes, regionId: string, search: string, push: boolean = true) {
        this.rankedModeType = mode
        this.leaderboardUrl = regionId
        this.searchTerm = search

        this.renderRankedTabs()
        this.renderHorizontalTabs()
        this.tableController.clearData()
        this.tableController.scrollToTop()
        this.searchTerm == LeaderboardConstants.emptyUsernameSearch ? void this.fetchScrollData() : void this.fetchSearchData()
    }

    private async fetchScrollData() {
        const isActiveSearchOngoing = this.searchTerm != '' && this.tableController.getRowCount() > 0
        if(isActiveSearchOngoing) return

        if(this.isLoadingDataAfterScroll) return
        this.startWaitingForBackendToRespond()
        const startRank = this.tableController.getRowCount() + 1
        const endRank = startRank + LeaderboardConstants.maximumLeaderboardAPIRankingRangeLimit - 1

        const data = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiLeaderboards.data(RankedModeTypes[this.rankedModeType], this.leaderboardUrl), undefined, {
            start: startRank,
            end: endRank,
            search: this.searchTerm,
        })
        this.stopWaitingForBackendToRespond()
        if(data == undefined) return

        if(this.leaderboardUrl == LeaderboardCategoryStringID.Friends && data.json.length == 0) {
            this.tableController.displayNoContentMessage()
            return
        }
        this.tableController.updateRankingData(data.json as LeaderboardRowData[], (newLeaderboardUrl: string) => this.onLeaderboardUrlChange(newLeaderboardUrl))
        this.stickyRowController.tableRowLoadingCompleted()
        this.tableController.updateStickyRowStyle()
    }

    private async fetchSearchData() {
        this.startWaitingForBackendToRespond()
        const data = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiLeaderboards.usernameSearch(RankedModeTypes[this.rankedModeType], this.leaderboardUrl), undefined, {
            usernameSearchInput: this.searchTerm,
        })
        this.stopWaitingForBackendToRespond()
        if(data == undefined) return

        this.tableController.clearData()
        this.tableController.scrollToTop()
        this.tableController.updateRankingData(data.json as LeaderboardRowData[], (newLeaderboardUrl: string) => this.onLeaderboardUrlChange(newLeaderboardUrl))
        this.stickyRowController.tableRowLoadingCompleted()
        this.tableController.updateStickyRowStyle()
    }

    private startWaitingForBackendToRespond() {
        this.isLoadingDataAfterScroll = true
        const maxTimeToWaitForBackend = TimeUtils.MILLISECONDS
        this.waitForBackend = setTimeout(() => {
            this.stopWaitingForBackendToRespond()
        }, maxTimeToWaitForBackend)
    }

    private stopWaitingForBackendToRespond() {
        this.isLoadingDataAfterScroll = false
        if(this.waitForBackend != undefined) clearTimeout(this.waitForBackend)
    }

    private async fetchNewTabs() {
        const response = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiLeaderboards.tabs(this.leaderboardUrl), undefined)
        if(response == undefined) return
        const leaderboardTabData = response.json as LeaderboardTabData
        this.updateTabData(leaderboardTabData)
    }

    private renderSeasonProgress(seasonData: RankedSeasonData) {
        this.seasonProgressView.render({
            startDate: new Date(seasonData.startDate),
            endDate: new Date(seasonData.endDate),
            size: 'sm',
        })
    }

    private updateTabData(leaderboardTabData: LeaderboardTabData) {
        this.tableTabs = leaderboardTabData.tableTabs
        this.renderHorizontalTabs()
        this.enabledTabIds = leaderboardTabData.activeRankedModeTypes.map((m) => RankedModeTypes[m])
        this.renderRankedTabs()
        if(leaderboardTabData.activeSeasonData != undefined) {
            this.renderSeasonProgress(leaderboardTabData.activeSeasonData)
        }
    }

    private renderRankedTabs() {
        this.rankedTabsView.render(UILeaderboardUtils.getRankedTabsProps({
            rankedModeType: this.rankedModeType,
            enabledTabIds: this.enabledTabIds,
            showRushTab: this.showRushTab,
            onRankedModeChange: (mode) => this.onRankedModeChange(mode),
        },
        {direction: 'horizontal', compactMode: true}))
    }

    private async initRushTab() {
        if(await UILeaderboardUtils.shouldShowRushTab()) {
            this.showRushTab = true
            this.renderRankedTabs()
        }
    }

    private renderHorizontalTabs() {
        this.tableTabsView.render({
            size: 'sm',
            activeTabId: this.leaderboardUrl,
            tabs: this.tableTabs.map((tab) => ({
                id: tab.leaderboardUrl,
                label: UILeaderboardUtils.tLeaderboardTabName(tab.label),
            })),
            onTabChange: (tabId: string) => this.onHorizontalTabChange(tabId),
        })
    }

    private async fetchMyStickyRowData() {
        const data = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiLeaderboards.stickyRowData(RankedModeTypes[this.rankedModeType], this.leaderboardUrl), undefined)
        if(data == undefined) return

        const myStickyRowData = data.json as LeaderboardStickyRowData | ''
        myStickyRowData == '' ? this.stickyRowController.clearStickyRowData() : this.stickyRowController.updateStickyRowData(myStickyRowData, this.tableController.tableContainer.clientHeight)
    }

    private static featureCheck() {
        const div = document.createElement('div')
        if(div.replaceChildren == undefined) {
            UIPopupController.create({key: 'strings:leaderboardPage.updateBrowser.title'}, {key: 'strings:leaderboardPage.updateBrowser.body'}, () => navigateToPage('/'), () => navigateToPage('/'), false)
            throw new Error('Failed to create leaderboard: Browser does not support required features.')
        }
    }
}
